/**
 * Copy for the delete confirmations on WellnessSettingsPage.
 *
 * Pure and data-only, matching deviceActionCopy.js — the wording lives in one
 * reviewable place and can be exercised without mounting the page.
 *
 * Returns:
 *   title        — dialog heading
 *   noun         — entity word, so the heading and the lead sentence can never
 *                  name different things
 *   consequence  — what stops working. Rendered as normal body text, not muted.
 *   closing      — reversibility note. Rendered muted and last.
 *
 * On alerts: `generate_alarm` is a per-window flag, so a check-in or rounds
 * window does not necessarily raise alerts. The wording therefore says "any
 * alerts it raises will stop" rather than promising alerts exist — true whether
 * the flag is set or not.
 */

export const WELLNESS_DELETE_KINDS = ['checkin', 'rounds', 'careflow'];

const NO_UNDO = 'This cannot be undone.';

/**
 * @param {string} kind — 'checkin' | 'rounds' | 'careflow'
 * @param {{count?: number}} [opts]
 * @returns {{title, noun, consequence, closing, confirmLabel, savingLabel}}
 */
export function describeWellnessDelete(kind, opts = {}) {
    const count = Number(opts.count) || 0;
    const many = count !== 1;
    const base = {
        confirmLabel: 'Delete',
        savingLabel: 'Deleting…',
    };

    switch (kind) {
        case 'checkin':
            return {
                ...base,
                noun: 'check-in window',
                title: `Delete ${count} Check-in Window${many ? 's' : ''}`,
                consequence:
                    `${many ? 'These windows' : 'This window'} will stop running. Residents in ` +
                    `${many ? 'their' : 'its'} assigned resident groups will no longer be checked in during ` +
                    `${many ? 'these times' : 'this time'}, and any alerts ${many ? 'they raise' : 'it raises'} ` +
                    `will stop.`,
                closing: `${NO_UNDO} The group assignments will be removed along with ${many ? 'the windows' : 'the window'}.`,
            };

        case 'rounds':
            return {
                ...base,
                noun: 'rounds window',
                title: `Delete ${count} Rounds Window${many ? 's' : ''}`,
                consequence:
                    `${many ? 'These windows' : 'This window'} will stop running. Rounds will no longer be ` +
                    `expected for ${many ? 'their' : 'its'} assigned area groups during ` +
                    `${many ? 'these times' : 'this time'}, and any alerts ${many ? 'they raise' : 'it raises'} ` +
                    `will stop.`,
                closing: `${NO_UNDO} The group assignments will be removed along with ${many ? 'the windows' : 'the window'}.`,
            };

        case 'careflow':
            return {
                ...base,
                noun: 'careflow',
                title: `Delete ${count} Careflow${many ? 's' : ''}`,
                consequence:
                    `Events handled by ${many ? 'these careflows' : 'this careflow'} will no longer trigger ` +
                    `${many ? 'their' : 'its'} configured outputs, so the notifications and calls ` +
                    `${many ? 'they send' : 'it sends'} will stop.`,
                closing: `${NO_UNDO} Only continue if you are certain ${many ? 'these careflows are' : 'this careflow is'} no longer needed.`,
            };

        default:
            throw new Error(`describeWellnessDelete: unknown kind "${kind}"`);
    }
}
